import React, { useEffect, useState, useMemo } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { format, isSameDay, addDays, parseISO, isWithinInterval, startOfWeek } from "date-fns";
import { el } from "date-fns/locale";
import { CalendarEvent } from "../types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { AlertCircle, Building, Calendar, User, Plus, ChevronDown, ChevronUp, X, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { generateEventColor, sortEventsByTitle } from "../utils/colorUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from '@/contexts/ThemeContext';
import { useColorIntensity } from '../contexts/ColorIntensityContext';
import { useWeekVisibility } from "../hooks/useWeekVisibility";
import { useUserRoles } from "@/hooks/useUserRoles";
import { identifyGuardsWithMultipleShifts } from "../utils/shiftUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";

interface TotalWorkCalendarProps {
  events: CalendarEvent[];
  currentDate: Date;
  dateRange: { start: Date; end: Date };
  guardId: string;
  handleDayClick: (date: Date) => void;
  handleEventClick: (event: CalendarEvent, e: React.MouseEvent) => void;
  defaultViewMode?: "week" | "month" | "day";
  showDuration?: boolean;
}

const TotalWorkCalendar: React.FC<TotalWorkCalendarProps> = ({
  events,
  currentDate,
  dateRange,
  guardId,
  handleDayClick,
  handleEventClick,
  defaultViewMode = "week",
  showDuration = false
}) => {
  const { t, locale } = useLanguage();
  const { start, end } = dateRange;
  const [conflictingEvents, setConflictingEvents] = useState<Record<string, boolean>>({});
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const { isAdmin } = useUserRoles();
  const { theme } = useTheme();
  
  // Get the events by date and filter by visibility
  const uniqueTitles = [...new Set(events.map(event => event.title))].sort();
  const { intensity } = useColorIntensity();
  
  const titleColors = useMemo(() => {
    const colors: Record<string, { backgroundColor: string, textColor: string }> = {};
    
    uniqueTitles.forEach(title => {
      const firstEvent = events.find(event => event.title === title);
      if (firstEvent) {
        colors[title] = generateEventColor(title, parseISO(firstEvent.start_date), theme, intensity);
      } else {
        colors[title] = generateEventColor(title, new Date(), theme, intensity);
      }
    });
    
    return colors;
  }, [uniqueTitles, events, theme, intensity]);
  
  // Identify guards with multiple shifts on the same day
  const guardsWithMultipleShifts = useMemo(() => {
    return identifyGuardsWithMultipleShifts(events);
  }, [events]);
  
  // Create a set of event IDs with multiple shifts for quick lookup
  const eventsWithMultipleShifts = useMemo(() => {
    const eventIds = new Set<string>();
    
    guardsWithMultipleShifts.forEach((eventIds2, guardId) => {
      eventIds2.forEach(eventId => {
        eventIds.add(eventId);
      });
    });
    
    return eventIds;
  }, [guardsWithMultipleShifts]);
  
  useEffect(() => {
    const conflicts: Record<string, boolean> = {};
    
    if (guardId !== 'all') return;
    
    const sortedEvents = [...events].sort((a, b) => 
      fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime()
    );
    
    for (let i = 0; i < sortedEvents.length; i++) {
      const currentEvent = sortedEvents[i];
      const currentStart = fromLocalISOString(currentEvent.start_date);
      const currentEnd = fromLocalISOString(currentEvent.end_date);
      
      for (let j = i + 1; j < sortedEvents.length; j++) {
        const otherEvent = sortedEvents[j];
        
        const currentGuards = currentEvent.assigned_guards || [];
        const otherGuards = otherEvent.assigned_guards || [];
        
        const commonGuards = currentGuards.filter(guard => otherGuards.includes(guard));
        
        if (commonGuards.length === 0) continue;
        
        const otherStart = fromLocalISOString(otherEvent.start_date);
        const otherEnd = fromLocalISOString(otherEvent.end_date);
        
        const isOverlapping = (
          (currentStart <= otherEnd && currentEnd >= otherStart) ||
          isWithinInterval(currentStart, { start: otherStart, end: otherEnd }) ||
          isWithinInterval(currentEnd, { start: otherStart, end: otherEnd }) ||
          isWithinInterval(otherStart, { start: currentStart, end: currentEnd }) ||
          isWithinInterval(otherEnd, { start: currentStart, end: currentEnd })
        );
        
        if (isOverlapping) {
          conflicts[currentEvent.id] = true;
          conflicts[otherEvent.id] = true;
        }
      }
    }
    
    setConflictingEvents(conflicts);
  }, [events, guardId]);
  
  const eventsByDate = events.reduce((acc, event) => {
    const startDate = parseISO(event.start_date);
    const dateStr = format(startDate, 'yyyy-MM-dd');
    
    if (!acc[dateStr]) {
      acc[dateStr] = [];
    }
    
    acc[dateStr].push(event);
    return acc;
  }, {} as Record<string, CalendarEvent[]>);
  
  Object.keys(eventsByDate).forEach(dateStr => {
    eventsByDate[dateStr] = sortEventsByTitle(eventsByDate[dateStr]);
  });
  
  const days = [];
  const startDate = start;
  const endDate = end;
  let day = startDate;
  
  while (day <= endDate) {
    days.push(new Date(day));
    day = addDays(day, 1);
  }
  
  const gridColumns = isMobile 
    ? 'grid-cols-1' 
    : (days.length <= 7 ? `grid-cols-${days.length}` : 'grid-cols-7');
    
  const handleEventCardClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };
  
  const formatGuardNames = (guardNames?: string[]) => {
    if (!guardNames || guardNames.length === 0) return '';
    if (guardNames.length === 1) return guardNames[0];
    return `${guardNames[0]} +${guardNames.length - 1}`;
  };
  
  // Update the mobile card rendering to also show team name in the title
  const renderMobileEventCard = (event: CalendarEvent, titleColor: { backgroundColor: string, textColor: string } | undefined) => (
    <Card
      key={event.id}
      className={cn(
        "p-2 cursor-pointer hover:shadow-md transition-shadow mx-auto",
        conflictingEvents[event.id] && "border-red-500",
        eventsWithMultipleShifts.has(event.id) && "border-purple-500 border-2"
      )}
      style={{ 
        backgroundColor: conflictingEvents[event.id] ? "#FEF2F2" : titleColor?.backgroundColor || '#f5f5f5',
        borderLeftWidth: "4px",
        borderLeftColor: conflictingEvents[event.id] ? "#EF4444" : 
                        eventsWithMultipleShifts.has(event.id) ? "#8B5CF6" : 
                        titleColor?.backgroundColor || '#f5f5f5',
        maxWidth: "95%",
        backgroundImage: eventsWithMultipleShifts.has(event.id) ? 
          'repeating-linear-gradient(45deg, rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.15) 5px, transparent 5px, transparent 15px)' : 'none'
      }}
      onClick={(e) => handleEventCardClick(event, e)}
    >
      <div className="font-medium flex flex-col items-center justify-center gap-1 text-black">
        {conflictingEvents[event.id] && <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />}
        <span className="truncate text-[0.75rem] leading-tight sm:text-sm font-medium">{event.title}</span>
        
        {/* Add team name right below the title */}
        {event.team_name && (
          <div className="flex items-center justify-center gap-1 text-[0.65rem] leading-tight sm:text-xs opacity-80">
            <Building className="h-2.5 w-2.5 min-w-[0.625rem] flex-shrink-0 opacity-70 text-black" />
            <span className="truncate">{event.team_name}</span>
          </div>
        )}
      </div>
      
      <div className="mt-2 space-y-1.5">
        <div className="flex items-center justify-center gap-1 text-[0.65rem] leading-tight sm:text-xs text-black">
          <Clock className="h-2.5 w-2.5 min-w-[0.625rem] flex-shrink-0 opacity-70 text-black" />
          <span>
            {format(parseISO(event.start_date), 'HH:mm')}-{format(parseISO(event.end_date), 'HH:mm')}
          </span>
        </div>
        
        {guardId === 'all' && event.guard_names && event.guard_names.length > 0 && (
          <div className="flex items-center justify-center gap-1 text-[0.65rem] leading-tight sm:text-xs text-black">
            <User className="h-2.5 w-2.5 min-w-[0.625rem] flex-shrink-0 opacity-70 text-black" />
            <span className="truncate">
              {formatGuardNames(event.guard_names)}
            </span>
          </div>
        )}
        
        {event.location && (
          <div className="flex items-center justify-center gap-1 text-[0.65rem] leading-tight sm:text-xs text-black">
            <div className="w-2.5 h-2.5 min-w-[0.625rem] opacity-70"></div>
            <span className="truncate">{event.location}</span>
          </div>
        )}
      </div>
    </Card>
  );
  
  function renderEventCard(event: CalendarEvent, titleColor: { backgroundColor: string, textColor: string } | undefined) {
    const hasConflict = conflictingEvents[event.id] || false;
    const hasNoGuards = !event.assigned_guards || event.assigned_guards.length === 0;
    const hasMultipleShifts = eventsWithMultipleShifts.has(event.id);
    
    let backgroundColor;
    let textColor;
    
    if (hasNoGuards) {
      backgroundColor = "#000000"; // Black background for no guards
      textColor = "#FFFFFF"; // White text for no guards
    } else if (hasConflict) {
      backgroundColor = "#FEF2F2"; // Light red for conflicts
      textColor = "#000000"; // Black text for conflicts
    } else {
      const { backgroundColor: bgColor } = titleColor || 
        generateEventColor(event.title, parseISO(event.start_date), theme);
      backgroundColor = bgColor;
      textColor = "#000000"; // Black text for normal events
    }
    
    // Diagonal line pattern for multiple shifts
    const diagonalPattern = hasMultipleShifts ? {
      backgroundImage: `repeating-linear-gradient(45deg, 
        rgba(139, 92, 246, 0.15), 
        rgba(139, 92, 246, 0.15) 5px, 
        transparent 5px, 
        transparent 15px)`
    } : {};
      
    return (
      <Card
        key={event.id}
        className={`p-2 cursor-pointer hover:shadow-md transition-shadow overflow-hidden ${
          hasConflict ? 'border-red-500' : hasMultipleShifts ? 'border-purple-500 border-2' : ''
        }`}
        style={{ 
          backgroundColor,
          color: textColor,
          borderLeftWidth: "4px",
          borderLeftColor: hasConflict ? "#EF4444" : 
                          hasMultipleShifts ? "#8B5CF6" : 
                          backgroundColor,
          ...diagonalPattern
        }}
        onClick={(e) => handleEventCardClick(event, e)}
      >
        <div className="font-medium truncate flex flex-col" style={{color: textColor}}>
          {hasConflict && <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />}
          
          {/* Title with team name */}
          <div className="flex items-start gap-1">
            <span className="truncate text-[0.75rem] leading-tight sm:text-sm font-medium">{event.title}</span>
          </div>
          
          {/* Team name badge right below the title */}
          {event.team_name && (
            <div className="flex items-center gap-1 mt-0.5">
              <Building className={`h-2.5 w-2.5 min-w-[0.625rem] flex-shrink-0 ${hasNoGuards ? "text-white" : "text-black"} opacity-70`} />
              <span className="text-[0.65rem] leading-tight sm:text-xs truncate opacity-80">{event.team_name}</span>
            </div>
          )}
        </div>
        
        <div className="text-[0.65rem] leading-tight sm:text-xs mt-1 truncate" style={{color: textColor}}>
          <span className="mr-1">{format(parseISO(event.start_date), 'HH:mm')}-{format(parseISO(event.end_date), 'HH:mm')}</span>
        </div>
        
        {showDuration && (
          <div className="text-[0.65rem] leading-tight sm:text-xs mt-1 flex items-center gap-1 truncate" style={{color: textColor}}>
            <Clock className={`h-2.5 w-2.5 min-w-[0.625rem] flex-shrink-0 ${hasNoGuards ? "text-white" : "text-black"} opacity-70`} />
            <span>
              {(() => {
                const startDate = parseISO(event.start_date);
                const endDate = parseISO(event.end_date);
                const durationMs = endDate.getTime() - startDate.getTime();
                const durationHours = durationMs / (1000 * 60 * 60);
                
                return durationHours % 1 === 0 
                  ? `${durationHours}hrs` 
                  : `${durationHours.toFixed(1)}hrs`;
              })()}
            </span>
          </div>
        )}
        
        {guardId === 'all' && event.guard_names && event.guard_names.length > 0 && (
          <div className="mt-1 flex items-center gap-1 truncate" style={{color: textColor}}>
            <User className={`h-2.5 w-2.5 min-w-[0.625rem] flex-shrink-0 ${hasNoGuards ? "text-white" : "text-black"} opacity-70`} />
            <span className="text-[0.65rem] leading-tight sm:text-xs font-medium truncate">
              {formatGuardNames(event.guard_names)}
            </span>
          </div>
        )}
      </Card>
    );
  }
  
  return (
    <div className="bg-background rounded-md border border-border dark:border-gray-700">
      <div className="p-4 bg-muted/20 border-b border-border dark:border-gray-700">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          {guardId === 'all' 
            ? (t("allGuardsShifts") || "All Guards Shifts") 
            : (t("totalWorkCalendar") || "Total Work Calendar")}
          <Badge variant="outline" className="ml-2">
            {events.length} {t("shifts") || "shifts"}
          </Badge>
        </h3>
      </div>
      
      {!isMobile ? (
        <>
          <div className={`grid ${gridColumns} bg-muted/20 border-b border-border dark:border-gray-700`}>
            {days.map(day => (
              <div 
                key={day.toString()} 
                className={cn(
                  "p-2 text-center border-r last:border-r-0 border-border dark:border-gray-700",
                  isSameDay(day, new Date()) && "bg-primary/10 font-bold"
                )}
              >
                <div className="font-medium">{format(day, 'EEE', { locale: locale === 'el' ? el : undefined })}</div>
                <div className={cn(
                  "text-sm", 
                  isSameDay(day, new Date()) && "text-primary font-bold"
                )}>
                  {format(day, 'd/M')}
                </div>
              </div>
            ))}
          </div>
          
          <div className={`grid ${gridColumns} divide-x divide-border dark:divide-gray-700`}>
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayEvents = eventsByDate[dateStr] || [];
              
              return (
                <div
                  key={day.toString()}
                  className={cn(
                    "min-h-[100px] p-1 cursor-pointer",
                    isSameDay(day, new Date()) && "bg-primary/5"
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  {dayEvents.length > 0 ? (
                    <div className="space-y-1">
                      {uniqueTitles.map(title => {
                        const titleEvents = dayEvents.filter(event => event.title === title);
                        if (titleEvents.length === 0) return null;
                        
                        const titleColor = titleColors[title];
                        
                        return (
                          <div key={`title-${title}`} className="space-y-1">
                            {titleEvents.map(event => renderEventCard(event, titleColor))}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-full w-full flex items-center justify-center flex-col gap-2">
                      <span className="text-muted-foreground text-xs opacity-50">
                        {t("noShifts") || "No shifts"}
                      </span>
                      <button 
                        className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-foreground flex items-center justify-center hover:bg-blue-200 dark:hover:bg-blue-800/30 transition-colors shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDayClick(day);
                        }}
                      >
                        <Plus className="h-5 w-5 text-foreground" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="divide-y">
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayEvents = eventsByDate[dateStr] || [];
            
            return (
              <div key={day.toString()} className="mb-4">
                <div 
                  className={cn(
                    "p-3 sticky top-0 z-10 bg-background text-center font-medium flex items-center justify-center border-b text-black",
                    isSameDay(day, new Date()) && "bg-primary/10"
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="flex items-center justify-center">
                    <div className="mr-2">{format(day, 'EEE', { locale: locale === 'el' ? el : undefined })}</div>
                    <div className={cn(
                      "text-sm", 
                      isSameDay(day, new Date()) && "text-primary font-bold"
                    )}>
                      {format(day, 'd/M')}
                    </div>
                  </div>
                  
                  {dayEvents.length === 0 && (
                    <button 
                      className="w-8 h-8 rounded-full bg-blue-100 text-black flex items-center justify-center hover:bg-blue-200 transition-colors shadow-sm ml-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDayClick(day);
                      }}
                    >
                      <Plus className="h-5 w-5 text-black" />
                    </button>
                  )}
                </div>
                
                <div className="p-2 min-h-[60px]">
                  {dayEvents.length > 0 ? (
                    <div className="space-y-2">
                      {uniqueTitles.map(title => {
                        const titleEvents = dayEvents.filter(event => event.title === title);
                        if (titleEvents.length === 0) return null;
                        
                        const titleColor = titleColors[title];
                        
                        return (
                          <div key={`title-${title}`} className="space-y-2">
                            {titleEvents.map(event => renderMobileEventCard(event, titleColor))}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-full w-full flex items-center justify-center p-4">
                      <span className="text-muted-foreground text-xs opacity-50">
                        {t("noShifts") || "No shifts"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="text-center w-full text-black">{selectedEvent?.title}</span>
              <DialogClose className="w-6 h-6 rounded-full hover:bg-muted flex items-center justify-center">
                <X className="h-4 w-4" />
              </DialogClose>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2">
                <Calendar className="h-4 w-4 text-black" />
                <div className="text-center text-black">
                  <div className="font-medium">
                    {format(parseISO(selectedEvent.start_date), 'PPP', { locale: locale === 'el' ? el : undefined })}
                  </div>
                  <div className="text-sm">
                    {format(parseISO(selectedEvent.start_date), 'HH:mm')} - {format(parseISO(selectedEvent.end_date), 'HH:mm')}
                  </div>
                </div>
              </div>
              
              {guardId === 'all' && selectedEvent.guard_names && selectedEvent.guard_names.length > 0 && (
                <div className="flex items-start justify-center gap-2">
                  <User className="h-4 w-4 text-black mt-0.5" />
                  <div className="text-center text-black">
                    <div className="font-medium mb-1">{t("assignedGuards") || "Assigned Guards"}</div>
                    <div className="flex flex-wrap justify-center gap-1">
                      {selectedEvent.guard_names.map((guardName, index) => (
                        <Badge key={index} variant="outline" className="bg-muted/40 text-black">
                          {guardName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Add indication for multiple shifts */}
              {eventsWithMultipleShifts.has(selectedEvent.id) && (
                <div className="flex items-center justify-center">
                  <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                    {t("guardMultipleShifts") || "Guard has multiple shifts on this day"}
                  </Badge>
                </div>
              )}
              
              {selectedEvent.team_name && (
                <div className="flex items-center justify-center gap-2">
                  <Building className="h-4 w-4 text-black" />
                  <div className="text-center text-black">
                    <div className="font-medium">{t("team") || "Team"}</div>
                    <div className="text-sm">{selectedEvent.team_name}</div>
                  </div>
                </div>
              )}
              
              {selectedEvent.location && (
                <div className="flex items-start justify-center gap-2">
                  <div className="h-4 w-4" />
                  <div className="text-center text-black">
                    <div className="font-medium">{t("location") || "Location"}</div>
                    <div className="text-sm">{selectedEvent.location}</div>
                  </div>
                </div>
              )}
              
              {selectedEvent.description && (
                <div className="flex items-start justify-center gap-2">
                  <div className="h-4 w-4" />
                  <div className="text-center text-black">
                    <div className="font-medium">{t("description") || "Description"}</div>
                    <div className="text-sm whitespace-pre-wrap">{selectedEvent.description}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TotalWorkCalendar;
